/* eslint-disable react-hooks/exhaustive-deps */
import Box from "@mui/material/Box";
import { theme } from "../../theme";
import FdTypography from "../UIComponents/Typography/FdTypography";
import { useForm } from "react-hook-form";
import FdCheckbox from "../UIComponents/Checkbox/FdCheckbox";
import FdButton from "../UIComponents/Button/FdButton";
import { alertForm as classes } from "./styles";
import FdTextField from "../UIComponents/TextField/FdTextfield";
import { Alert, ICreateAlert } from "./type";
import { useContext, useEffect, useRef } from "react";
import { APIContext } from "../../store/APIStore";
import { StoreContext } from "../../store/Store";
import { useNavigate, useParams } from "react-router-dom";
import RouteLeavingGuard from "../RouteLeavingGuard/RouteLeavingGuard";
import LabelWithTooltip from "../LabelWithTooltip/LabelWithTooltip";
import NotificationMessage from "../NotificationMessage/NotificationMessage";
import { notificationType } from "../UIComponents/Alert/FdAlert";
import { useHasPermissions } from "../../hooks/useHasPermissions";
import { PERMISSIONS } from "../../roles";

const AlertForm = (props: Alert) => {
  const { fetchAlertData, alertModify, setSelectedId } = props;
  let navigate = useNavigate();
  const api = useContext(APIContext);
  const [state] = useContext(StoreContext);
  const { selectedAlert } = state;
  const { eventId } = useParams() as any;
  const notificationRef = useRef<any>();
  const initialValues = {
    name: selectedAlert?.name || "",
    content: selectedAlert?.content || "",
    blocking: selectedAlert?.blocking || false,
    trigger: selectedAlert?.trigger || "",
    thirdPartyId: selectedAlert?.thirdPartyId || "",
    location: selectedAlert?.location || null,
    disabled: selectedAlert?.disabled ? false : true,
    predicate: (selectedAlert?.predicate as any)?.expression ?? ""
  };
  const canViewInternalApi = useHasPermissions(
    PERMISSIONS.CAN_VIEW_INTERNAL_API
  );
  const {
    register,
    handleSubmit,
    formState: { isDirty, errors },
    reset,
    watch,
    control,
  } = useForm({
    defaultValues: initialValues,
  });

  const watchFields = watch();
  const createAlert = async (values: ICreateAlert) => {
    try {
      const alertSuccess = (await api.alertService.createEvent(
        { ...values, disabled: values.disabled ? false : true },
        eventId
      )) as unknown as ICreateAlert;
      fetchAlertData();
      setSelectedId(alertSuccess?.id);
      notificationRef.current?.show(
        "Alert created successfully saved",
        notificationType.Success
      );
    } catch (error) {
      console.log(error);
    }
  };

  const updateAlert = async (values: ICreateAlert) => {
    try {
      const alertSuccess = (await api.alertService.updateEvent(
        { ...values, disabled: values.disabled ? false : true },
        eventId,
        selectedAlert.id
      )) as unknown as ICreateAlert;
      fetchAlertData();
      setSelectedId(alertSuccess?.id);
      notificationRef.current?.show(
        "Alert updated successfully saved",
        notificationType.Success
      );
    } catch (error) {
      console.log(error);
    }
  };

  const onSubmit = async (values: any) => {
    const { predicate, ...rest } = values;
    const expression = (predicate || "").trim();

    const payload = {
      ...rest          
    };

    if(expression) {
      payload["predicate"] = {
        type: 'JMESPath',
        expression,
      }
    }

    if (alertModify?.createAlert) createAlert(payload);
    else updateAlert(payload);
  };
  
  useEffect(() => {
    reset(initialValues);
  }, [reset, props, state]);
  return (
    <>
      <Box
        sx={[
          classes.root,
          {
            border: !selectedAlert?.id
              ? `5px solid ${theme.palette.primary.main}`
              : undefined,
          },
        ]}
      >
        <Box sx={classes.infoTitle}>
          <Box textAlign="left">
            <FdTypography
              label={watchFields.name || "New Alert"}
              variant="h2"
              component="h2"
              sx={{ marginBottom: "10px" }}
            />
          </Box>
        </Box>
        <Box sx={classes.infoFields}>
          <form
            id="alertForm"
            onSubmit={handleSubmit(onSubmit)}
            noValidate
            style={{ width: "100%", textAlign: "left" }}
          >
            <Box sx={classes.formRoot}>
              {/* title */}
              <Box marginBottom="15px">
                <FdTextField
                  register={register}
                  name="name"
                  label="Title"
                  required
                  error={!!errors.name}
                  errorMsg={errors.name && "Alert title is required"}
                />
              </Box>
              {/* Enabled */}
              <Box marginBottom="15px">
                <FdCheckbox
                  name="disabled"
                  register={register}
                  value={initialValues.disabled}
                  defaultValue={true}
                  className={`${classes.checkbox}`}
                  control={control}
                  label={
                    <LabelWithTooltip
                      label="Enabled (if unchecked, alert will be ignored)"
                      hint="If not enabled, the alert is ignored during check-ins. Use this instead of deleting if you think you might need it again in the future."
                    />
                  }
                />
              </Box>
              {/* message */}
              <Box marginBottom="15px">
                <FdTextField
                  register={register}
                  name="content"
                  label="Message"
                  multiline
                  rows={3}
                  required
                  error={errors?.content?.type === "required" || errors?.content?.type === "maxLength"}
                  errorMsg={(errors?.content?.type === "required"
                  ? "Alert message is required"
                  : "") ||
                (errors?.content?.type === "maxLength"
                  ? "Maximum length: 200 characters"
                  : "")}
                  validationObject={
                    {
                      maxLength: (v: string | any[]) => v.length <= 200,
                    }
                  }
                  helperText={<Box sx={{color:"#808080",mt:"5px",fontSize:"12px"}}>{watch("content").length}/200 characters</Box>}
                />
              </Box>

              {/* printing */}
              <Box marginBottom="15px">
                <FdCheckbox
                  name="blocking"
                  register={register}
                  value={initialValues.blocking}
                  defaultValue={initialValues.blocking}
                  className={`${classes.checkbox}`}
                  control={control}
                  label={
                    <LabelWithTooltip
                      label="Prevent badge from printing"
                      hint="Prevent check-in to complete (no badge will be printed)"
                    />
                  }
                />
              </Box>
              {/* trigger */}
              <Box marginBottom="15px">
                <FdTextField
                  register={register}
                  name="trigger"
                  label="Trigger for fdOnsite legacy"
                  multiline
                  rows={3}
                  error={!!errors.trigger}
                  errorMsg={errors.trigger && "Trigger is required"}
                  disabled={!canViewInternalApi}
                />
              </Box>
              {/* trigger */}
              <Box marginBottom="15px">
                <FdTextField
                  register={register}
                  name="thirdPartyId"
                  label="Third party ID"
                  disabled={alertModify?.updateAlert}
                />
              </Box>
              {/* message */}
              <Box marginBottom="15px">
                <FdTextField
                  register={register}
                  name="location"
                  label={
                    <LabelWithTooltip
                      label="Location filter (optional)"
                      hint="Use this to ignore the alert on kiosks that do not match the entered location (based on the onsite location input)"
                    />
                  }
                />
              </Box>
              {/* Predicate Start */}
              <Box marginBottom="15px">
                <FdTextField
                  register={register}
                  name="predicate"
                  label={
                    <LabelWithTooltip
                       label="Trigger for fdOnsite revamped"
                       hint="Use this field to apply rules for fdOnsite revamp"
                    />
                  }
                  multiline
                  rows={3}
                  error={errors?.predicate?.type === "maxLength"}
                  errorMsg={
                    errors?.predicate?.type === "maxLength"
                      ? "Maximum length: 2000 characters"
                      : ""
                  }
                  validationObject={{
                    maxLength: (v: string | any[]) => !v || v.length <= 2000,
                  }}
                />
              </Box>
              {/* Predicate end */}
            </Box>
          </form>
          <RouteLeavingGuard
            when={isDirty}
            navigate={(path) => navigate(path)}
            shouldBlockNavigation={(location) => isDirty}
          />
          <NotificationMessage ref={notificationRef} />
        </Box>

        <FdButton
          sx={classes.saveButton}
          form="alertForm"
          type="submit"
          ButtonName="Save"
        />
      </Box>
    </>
  );
};

export default AlertForm;
